/**
 * Infobalken über dem Header (sections/mio-infobalken.liquid).
 *
 * Wechselt die Meldungen im festen Takt. Pausiert, solange die Maus darüber
 * liegt, ein Element darin den Tastaturfokus hat oder der Tab im Hintergrund
 * ist. Bei reduzierter Bewegung kein Selbstwechsel.
 * Der Code-Knopf kopiert den Rabattcode und meldet das kurz im Knopf selbst.
 */

const MQ_REDUZIERT = '(prefers-reduced-motion: reduce)';
const KOPIERT_ZEIGEN_MS = 1600;
const INTERVALL_MINDESTENS_MS = 3000;

/** Kopiert Text; fällt ohne Clipboard-API auf ein verstecktes Textfeld zurück. */
async function kopieren(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Ohne Berechtigung (z. B. im Theme-Editor-Rahmen) weiter unten versuchen.
    }
  }
  const feld = document.createElement('textarea');
  feld.value = text;
  feld.setAttribute('readonly', '');
  feld.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
  document.body.append(feld);
  feld.select();
  let geklappt = false;
  try {
    geklappt = document.execCommand('copy');
  } catch {
    geklappt = false;
  }
  feld.remove();
  return geklappt;
}

class MioInfobalken extends HTMLElement {
  connectedCallback() {
    this.meldungen = [...this.querySelectorAll('[data-meldung]')];
    this.status = this.querySelector('[data-status]');
    this.index = 0;
    this.pausen = new Set();
    this.takt = 0;

    this.addEventListener('click', (e) => this.codeKlick(e));
    if (this.meldungen.length < 2) return;

    this.meldungen.slice(1).forEach((m) => m.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1')));

    this.intervall = Math.max(INTERVALL_MINDESTENS_MS, Number(this.dataset.intervall) || 5000);
    this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.pausieren('maus'));
    this.addEventListener('pointerleave', () => this.fortsetzen('maus'));
    this.addEventListener('focusin', () => this.pausieren('fokus'));
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.fortsetzen('fokus');
    });
    this.sichtbarkeit = () => (document.hidden ? this.pausieren('tab') : this.fortsetzen('tab'));
    document.addEventListener('visibilitychange', this.sichtbarkeit);

    // Im Theme-Editor die gewählte Meldung zeigen.
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.meldungen.indexOf(e.target);
      if (i >= 0) this.zeigen(i);
      this.pausieren('editor');
    });
    this.addEventListener('shopify:block:deselect', () => this.fortsetzen('editor'));

    this.bewegung = window.matchMedia(MQ_REDUZIERT);
    this.bewegungWechsel = () => this.planen();
    this.bewegung.addEventListener('change', this.bewegungWechsel);
    this.planen();

    // Ohne Selbstwechsel zeigt ein Tippen auf den Balken die nächste Meldung,
    // sonst bliebe der Rabattcode für diese Besucher unsichtbar.
    this.addEventListener('click', (e) => {
      if (this.bewegung.matches && !e.target.closest('a, [data-code]')) {
        this.zeigen((this.index + 1) % this.meldungen.length);
      }
    });
  }

  disconnectedCallback() {
    clearTimeout(this.takt);
    if (this.sichtbarkeit) document.removeEventListener('visibilitychange', this.sichtbarkeit);
    this.bewegung?.removeEventListener('change', this.bewegungWechsel);
  }

  pausieren(grund) {
    this.pausen.add(grund);
    this.planen();
  }

  fortsetzen(grund) {
    this.pausen.delete(grund);
    this.planen();
  }

  planen() {
    clearTimeout(this.takt);
    this.takt = 0;
    if (this.pausen.size > 0 || this.bewegung.matches) return;
    this.takt = setTimeout(() => {
      this.zeigen((this.index + 1) % this.meldungen.length);
      this.planen();
    }, this.intervall);
  }

  zeigen(neu) {
    if (neu === this.index) return;
    const alt = this.meldungen[this.index];
    const naechste = this.meldungen[neu];
    alt.classList.remove('is-aktiv');
    alt.classList.add('is-weg');
    naechste.classList.remove('is-weg');
    naechste.classList.add('is-aktiv');
    // Nur die sichtbare Meldung ist mit Tab erreichbar.
    alt.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    naechste.querySelectorAll('a, button').forEach((el) => el.removeAttribute('tabindex'));
    this.index = neu;
  }

  async codeKlick(e) {
    const knopf = e.target.closest('[data-code]');
    if (!knopf || knopf.dataset.zeigt) return;
    const code = knopf.dataset.code;
    const geklappt = await kopieren(code);
    if (!geklappt) return;

    knopf.dataset.zeigt = '1';
    knopf.textContent = knopf.dataset.kopiert;
    if (this.status) this.status.textContent = `${code}: ${knopf.dataset.kopiert}`;
    setTimeout(() => {
      knopf.textContent = code;
      delete knopf.dataset.zeigt;
      if (this.status) this.status.textContent = '';
    }, KOPIERT_ZEIGEN_MS);
  }
}

if (!customElements.get('mio-infobalken')) customElements.define('mio-infobalken', MioInfobalken);
